import { setRecentCities } from 'app/store/reducers/location';
import { AppDispatch, RootState } from 'app/hooks/appHooks';
import {
  optionsSelector,
  distribusionEnabledSelector,
  recentCitiesCookieSelector,
  showStationSuggestionSelector,
  flixTrainSelector,
} from 'app/store/selectors';
import { fetchAutocompleteResults } from 'app/providers/fetchAutocompleteResults';
import { AutocompleteResult } from 'app/models/types/store/location';
import { getCookieParsed } from 'app/storage/coookie';
import { createAsyncThunk } from '@reduxjs/toolkit';

export const retrieveRecentCities = createAsyncThunk<
  void,
  void,
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>('locationRetrieveRecentCities', async (_, { dispatch, getState }) => {
  const state = getState();
  const cookies: { from: string; to: string } = recentCitiesCookieSelector(state);
  const recentFromCities = getCookieParsed<AutocompleteResult[]>(cookies['from']) || [];
  const recentToCities = getCookieParsed<AutocompleteResult[]>(cookies['to']) || [];
  const uniqueCities: Pick<AutocompleteResult, 'id' | 'name'>[] = Object.values(
    [...recentFromCities, ...recentToCities].reduce((acc, curr) => {
      acc[curr.id] = curr;
      return acc;
    }, {}),
  );
  const { autocompleteUrl, locale } = optionsSelector(state);
  const distribusionEnabled: boolean = distribusionEnabledSelector(state);
  const showStationSuggestions: boolean = showStationSuggestionSelector(state);
  const isFlixTrainDomain: boolean = flixTrainSelector(state);

  const requests: Promise<AutocompleteResult | undefined>[] = [];
  for (let i = 0; i < uniqueCities.length; i++) {
    const city = uniqueCities[i];
    requests.push(
      fetchAutocompleteResults({
        autocompleteUrl,
        query: encodeURIComponent(city.name),
        locale,
        flixbusCitiesOnly: !distribusionEnabled,
        isFlixTrainDomain,
        showStationSuggestions,
        departureCity: null,
        popularStationsCount: null,
      })
        .then((cities) => cities.find(({ id }) => id === city.id))
        .catch(() => undefined),
    );
  }
  const results: (AutocompleteResult | undefined)[] | object = await Promise.all(requests).then(
    (res) => {
      return res.reduce((acc, curr) => {
        if (!curr) return acc;
        acc[curr.id] = curr;
        return acc;
      }, {});
    },
  );
  const from: AutocompleteResult[] = recentFromCities
    .map(({ id }) => results[id] || null)
    .filter((n) => n != null)
    .slice(0, 5);
  const to: AutocompleteResult[] = recentToCities
    .map(({ id }) => results[id] || null)
    .filter((n) => n != null)
    .slice(0, 5);
  dispatch(setRecentCities({ recentCities: { from, to } }));
});
